import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import FileUpload from "@/components/FileUpload";
import { Plus, Pencil, Trash2, X, Save, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
    title: "", url: "", type: "website", short_description: "",
    description: "", screenshot_url: "", tags: [], sort_order: 0, active: true,
};

const TYPES = [
    { value: "website", label: "Website" },
    { value: "ecommerce", label: "E-commerce" },
    { value: "platform", label: "Plataforma" },
    { value: "saas", label: "SaaS" },
];


export default function AdminPortfolio() {
    const [list, setList] = useState([]);
    const [edit, setEdit] = useState(null);

    const load = () => api.get("/admin/portfolio").then((r) => setList(r.data));
    useEffect(() => { load(); }, []);

    const save = async (data) => {
        try {
            const payload = {
                ...data,
                sort_order: Number(data.sort_order || 0),
                tags: typeof data.tags === "string"
                    ? data.tags.split(",").map((s) => s.trim()).filter(Boolean)
                    : (data.tags || []),
            };
            if (data.id) await api.put(`/admin/portfolio/${data.id}`, payload);
            else await api.post("/admin/portfolio", payload);
            toast.success("Projecto guardado");
            setEdit(null);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Erro");
        }
    };

    const del = async (id) => {
        if (!window.confirm("Eliminar projecto do portfolio?")) return;
        await api.delete(`/admin/portfolio/${id}`);
        load();
    };

    return (
        <div data-testid="admin-portfolio-page">
            <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                <div>
                    <h1 className="font-display font-black text-4xl tracking-tighter">Portfolio</h1>
                    <p className="text-sm text-ink/60 mt-1">{list.length} projectos exibidos em /servicos</p>
                </div>
                <button onClick={() => setEdit({ ...EMPTY })} className="btn-primary" data-testid="new-portfolio-btn">
                    <Plus size={16} /> Novo projecto
                </button>
            </div>

            <div className="grid lg:grid-cols-2 gap-4">
                {list.map((p) => (
                    <div key={p.id} className="border-2 border-ink bg-white flex" data-testid={`portfolio-row-${p.id}`}>
                        {p.screenshot_url ? (
                            <img src={p.screenshot_url} alt={p.title} className="w-32 h-32 object-cover border-r-2 border-ink" />
                        ) : (
                            <div className="w-32 h-32 bg-paper border-r-2 border-ink flex items-center justify-center text-xs font-mono text-ink/40">Sem imagem</div>
                        )}
                        <div className="flex-1 p-4 flex flex-col">
                            <div className="flex items-start justify-between gap-2">
                                <div>
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className="text-[10px] font-mono uppercase bg-magenta text-white px-2 py-0.5">{p.type}</span>
                                        {!p.active && <span className="text-[10px] font-mono uppercase border-2 border-ink/30 text-ink/50 px-2 py-0.5">Oculto</span>}
                                    </div>
                                    <h3 className="font-display font-bold text-lg">{p.title}</h3>
                                    <a href={p.url} target="_blank" rel="noopener noreferrer" className="text-xs font-mono text-ink/60 hover:text-magenta inline-flex items-center gap-1">
                                        {p.url} <ExternalLink size={10} />
                                    </a>
                                </div>
                                <div className="flex gap-1 shrink-0">
                                    <button onClick={() => setEdit(p)} className="border-2 border-ink p-2 hover:bg-ink hover:text-white" data-testid={`edit-${p.id}`}><Pencil size={14} /></button>
                                    <button onClick={() => del(p.id)} className="border-2 border-rose-500 text-rose-500 p-2 hover:bg-rose-500 hover:text-white"><Trash2 size={14} /></button>
                                </div>
                            </div>
                            <p className="text-xs text-ink/70 mt-2 line-clamp-2">{p.short_description}</p>
                        </div>
                    </div>
                ))}
            </div>

            {edit && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setEdit(null)}>
                    <div className="bg-white border-2 border-ink max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                        <div className="border-b-2 border-ink p-4 flex items-center justify-between">
                            <h2 className="font-display font-black text-xl tracking-tighter">{edit.id ? "Editar" : "Novo"} projecto</h2>
                            <button onClick={() => setEdit(null)}><X size={18} /></button>
                        </div>
                        <div className="p-6 space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <label className="block">
                                    <div className="label-mono mb-2">Título *</div>
                                    <input value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })} className="input-edge" data-testid="pf-title" />
                                </label>
                                <label className="block">
                                    <div className="label-mono mb-2">URL *</div>
                                    <input type="url" value={edit.url} onChange={(e) => setEdit({ ...edit, url: e.target.value })} className="input-edge" placeholder="https://..." data-testid="pf-url" />
                                </label>
                                <label className="block">
                                    <div className="label-mono mb-2">Tipo</div>
                                    <select value={edit.type} onChange={(e) => setEdit({ ...edit, type: e.target.value })} className="input-edge">
                                        {TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                                    </select>
                                </label>
                                <label className="block">
                                    <div className="label-mono mb-2">Ordem</div>
                                    <input type="number" value={edit.sort_order} onChange={(e) => setEdit({ ...edit, sort_order: e.target.value })} className="input-edge" />
                                </label>
                            </div>
                            <label className="block">
                                <div className="label-mono mb-2">Descrição curta (1-2 frases)</div>
                                <textarea value={edit.short_description} onChange={(e) => setEdit({ ...edit, short_description: e.target.value })} className="input-edge min-h-[60px]" data-testid="pf-short" />
                            </label>
                            <label className="block">
                                <div className="label-mono mb-2">Descrição completa</div>
                                <textarea value={edit.description} onChange={(e) => setEdit({ ...edit, description: e.target.value })} className="input-edge min-h-[120px]" data-testid="pf-desc" />
                            </label>
                            <label className="block">
                                <div className="label-mono mb-2">Tags (separadas por vírgula)</div>
                                <input
                                    value={Array.isArray(edit.tags) ? edit.tags.join(", ") : edit.tags}
                                    onChange={(e) => setEdit({ ...edit, tags: e.target.value })}
                                    className="input-edge"
                                    placeholder="E-commerce, B2B, Ifthenpay"
                                />
                            </label>
                            <div className="block">
                                <div className="label-mono mb-2">Screenshot / Capa</div>
                                <FileUpload
                                    value={edit.screenshot_url}
                                    onChange={(url) => setEdit({ ...edit, screenshot_url: url })}
                                />
                            </div>
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input type="checkbox" checked={edit.active} onChange={(e) => setEdit({ ...edit, active: e.target.checked })} />
                                <span className="text-sm font-bold uppercase tracking-wider">Visível</span>
                            </label>
                        </div>
                        <div className="border-t-2 border-ink p-4 flex justify-end gap-2">
                            <button onClick={() => setEdit(null)} className="btn-secondary">Cancelar</button>
                            <button onClick={() => save(edit)} className="btn-primary" data-testid="pf-save"><Save size={16} /> Guardar</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
